import Button from "@/common/components/Button";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import Modal from "@/common/components/Modal";
import { BriefcaseBusinessIcon } from "lucide-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { useQueryClient } from "@tanstack/react-query";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { useEffect, useMemo, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import GovernmentAllocationDistributionProductCategoriesForm from "./ProductCategoriesForm";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import useCreateGovernmentAllocationDistribution from '../../hooks/useCreateGovernmentAllocationDistribution';
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema, getGovernmentAllocationDistributionSchema } from '../../schemas/createGovernmentAllocationDistribution.schema';
import { CompanyGovernmentAllocationDistribution } from "@/features/governmentAllocations/types/governmentAllocationDistribution.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import { GovernmentAllocationTransformer } from "@/features/governmentAllocations/services/governmentAllocationsTransformer";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from "@/features/governmentAllocations/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts";
import { GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationMovementsKind } from "@/features/governmentAllocations/types/governmentAllocation.type";
import { validateDistributionLimits } from "@/features/governmentAllocations/utils/validateDistributionLimits";

interface GovernmentAllocationDistributionProductCategoriesModalProps {
  companyBranch?: CompanyBranchData;
  setCompanyBranch: React.Dispatch<React.SetStateAction<CompanyBranchData | undefined>>;
  governmentAllocationId?: string;
  distribution: CompanyGovernmentAllocationDistribution;
  isConsumptionControlledByQuantity?: boolean;
  movementsKind?: GovernmentAllocationMovementsKind;
}

const GovernmentAllocationDistributionCreateModal = ({
  companyBranch,
  setCompanyBranch,
  governmentAllocationId,
  distribution,
  isConsumptionControlledByQuantity,
  movementsKind = 'common',
}: GovernmentAllocationDistributionProductCategoriesModalProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const schema = useMemo(
    () => getGovernmentAllocationDistributionSchema(isConsumptionControlledByQuantity),
    [isConsumptionControlledByQuantity],
  );

  const {
    data: allowedCategoriesAndProducts,
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId,
    filters: { companyBranchId: companyBranch?.id },
  });

  const {
    mutate: createGovernmentAllocationDistribution,
    isPending: createGovernmentAllocationDistributionIsPending,
  } = useCreateGovernmentAllocationDistribution({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsDistributions', governmentAllocationId] });
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsAllowedCategoriesAndProducts'] });
      queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocation'] });
      setCompanyBranch(undefined);
      reset();
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: GovernmentAllocationTransformer.errorsTransformer(),
      })(error);
    },
  });

  const formMethods = useForm<CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      movements_kind: movementsKind,
      ...(isConsumptionControlledByQuantity
        ? { total_quantity: formatDecimalUnit(0), product_categories_distributions: [] }
        : { total_value: formatCurrency(0), product_categories_distributions: [] })
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = formMethods;

  const onSubmit: SubmitHandler<
    | CreateGovernmentAllocationDistributionValidationSchema
    | CreateGovernmentAllocationDistributionQuantityValidationSchema
  > = (form) => {
    if (allowedCategoriesAndProducts) {
      const isValid = validateDistributionLimits({
        form,
        allowedCategoriesAndProducts,
        isConsumptionControlledByQuantity: !!isConsumptionControlledByQuantity,
        setError,
      });

      if (!isValid) return;
    }

    createGovernmentAllocationDistribution({
      governmentAllocationId: governmentAllocationId,
      branchId: companyBranch?.id,
      form,
      isConsumptionControlledByQuantity,
    });
  };

  const productCategoriesDistributions = watch('product_categories_distributions');

  useEffect(() => {
    reset({
      movements_kind: movementsKind,
      ...(isConsumptionControlledByQuantity
        ? { total_quantity: formatDecimalUnit(0), product_categories_distributions: [] }
        : { total_value: formatCurrency(0), product_categories_distributions: [] })
    });
  }, [companyBranch?.id || '']);

  return (
    <>
      <Modal
        isOpen={!!companyBranch}
        setIsOpen={(value) => {
          if (!value) {
            setCompanyBranch(undefined);

            setTimeout(reset, 500);
          }
        }}
        title="Detalhando crédito da filial"
        showClose
        className="min-w-[640px] overflow-hidden"
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={companyBranch?.general_information?.company_logo?.url}
                displayName={companyBranch?.display_name}
                imageClassName="size-8"
                iconClassName="size-6"
              />

              <div className="max-h-[480px] min-h-[320px] overflow-auto flex flex-col gap-6 pe-2">
                {isConsumptionControlledByQuantity ? (
                  <UnitTextField
                    label="Quantidade limite"
                    decimals={2}
                    autoCorrect="off"
                    forceValue
                    {...register('total_quantity' as 'total_value')}
                    value={watch('total_quantity')}
                    errorMessage={(errors as { total_quantity?: { message?: string } }).total_quantity?.message || undefined}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                ) : (
                  <CurrencyTextField
                    label="Valor limite"
                    {...register('total_value')}
                    errorMessage={(errors as { total_value?: { message?: string } }).total_value?.message || undefined}
                    forceValue
                    value={watch(`total_value`)}
                    disabled={productCategoriesDistributions?.length > 0}
                  />
                )}

                {movementsKind !== 'fee' && (
                  <GovernmentAllocationDistributionProductCategoriesForm
                    distribution={distribution}
                    companyBranchId={companyBranch?.id}
                    isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
                  />
                )}
              </div>

              <Button onClick={handleSubmit(onSubmit)} disabled={createGovernmentAllocationDistributionIsPending}>
                Salvar
              </Button>
            </div>
          </FormProvider>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default GovernmentAllocationDistributionCreateModal;
